import React, { useEffect, useState, useRef } from "react";
import { userInfo, driverBoardTypes } from "../../../types/Types";
import {
  VStack,
  HStack,
  Box,
  IconButton,
  Text,
  Divider,
  Textarea,
  Checkbox,
  useToast,
} from "@chakra-ui/react";

// Import API
import { apiRead, apiWrite } from "../../../api/driverBoardAPI";
// Import Icons
import { IoIosSend } from "react-icons/io";

interface noteType {
  ID: number | null;
  DriverCode: string | null;
  Notes: string | null;
  Created: string | null;
  CreatedBy: string | null;
  CreatedByEmail: string | null;
}

interface notesProps {
  userEmail: string;
  userName: string;
  userDepartment: string;
  id: string | number;
  profileData: driverBoardTypes | null;
  isApprover: boolean;
  isRecruiter: boolean;
}

const DriverBoardApprovalNotes: React.FC<notesProps> = ({
  userName,
  userEmail,
  userDepartment,
  id,
  profileData,
  isApprover,
  isRecruiter,
}) => {
  // Note Section
  const [noteLoading, setNoteLoading] = useState<boolean>(false);
  const [data, setData] = useState<noteType[]>([]);
  const endOfNotesRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState<string>("");
  const [emailContacts, setEmailContacts] = useState<string[]>([]);
  const [recruiterChecked, setRecruiterChecked] = useState<boolean>(false);
  const [processorChecked, setProcessorChecked] = useState<boolean>(false);
  const toast = useToast();
  const recruiter = profileData?.RecruiterEmail || "";
  const processor = profileData?.ProcessorEmail || "";
    // changes made on 4th December 2025 for PS-8862
    const austineEmail = "Austin.stubbs@hirschbach.com";
    const ashleyEmail = "ashley.leitru@hirschbach.com";

  // Fetch Trainer Notes
  const fetchNotes = async (driverCode: string) => {
    setNoteLoading(true);
    setData([]);
    try {
      const response = await apiRead({
        process: "fetch driver board approval notes",
        id: id.toString(),
      });
      setData(response);
      setNoteLoading(false);
    } catch (error) {
      console.error("Error fetching notes:", error);
      setNoteLoading(false);
    }
  };

  // Add the note to the database
  const handleSendNote = async () => {
    if (!id || !notes.trim()) return;
    if (recruiterChecked && !emailContacts.includes(recruiter)) {
      toast({
        title: "Recruiter Email is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (processorChecked && !emailContacts.includes(processor)) {
      toast({
        title: "Processor Email is missing",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    setNoteLoading(true);

      let emailRecipients = emailContacts.join(", ");
      /* code commented out for ENG-6290 and logic added to include both austine and ashley in email recipients if either of them is selected as recipient,
         as per request from Austin Stubbs on 4th December 2025 for PS-8862
      // changes made on 4th December 2025 for PS-8862
      if (userEmail) {
          const tempUserEmail = userEmail.toLowerCase();
          const austine = austineEmail.toLowerCase();
          const ashley = ashleyEmail.toLowerCase();
          const extraEmail =
              tempUserEmail === austine
                  ? ashleyEmail
                  : tempUserEmail === ashley
                      ? austineEmail
                      : null;
          if (extraEmail) {
              emailRecipients += `, ${extraEmail}`;
          }
      }
      */

    try {
      await apiWrite({
        Process: "add driver board approval notes",
        id,
        Notes: notes,
        userName: userName,
        userEmail: userEmail,
        emailRecipients: emailRecipients,
        form: {
          FirstName: profileData?.FirstName,
          LastName: profileData?.LastName,
          OrientationLocation: profileData?.OrientationLocation,
          OrientationDate: profileData?.OrientationDate,
        },
      });
      setNotes("");
      setRecruiterChecked(false);
      setProcessorChecked(false);
      if (id) {
        fetchNotes(id.toString());
      }
    } catch (error) {
      console.error("Error sending note:", error);
    } finally {
      setNoteLoading(false);
    }
  };

  useEffect(() => {
    if (data.length > 0) {
      endOfNotesRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [data]);

  useEffect(() => {
    let updatedEmailContacts: string[] = [];
    if (recruiterChecked) {
      updatedEmailContacts.push(recruiter);
    }
    if (processorChecked) {
      updatedEmailContacts.push(processor);
    }
    setEmailContacts(updatedEmailContacts);
  }, [recruiterChecked, processorChecked]);

  // userEffect to fetch note
  useEffect(() => {
    if (id) {
      fetchNotes(id.toString());
    }
  }, [id]);

  return (
    <VStack width="100%" height="calc(100vh - 170px)" spacing={4}>
      <Box width="100%" height="100%" overflowY="auto" pb="20px">
        {data
          .sort(
            (a, b) =>
              new Date(a.Created as string).getTime() -
              new Date(b.Created as string).getTime()
          )
          .slice(0, 20)
          .map((note, index) => (
            <Box key={note.ID}>
              <Text fontWeight="semibold" fontSize="xs" color="blue.600">
                {note.CreatedBy} &middot;{" "}
                {new Date(note.Created as string).toLocaleString()}
              </Text>
              <Text mb={2} mt={2} fontSize="md">
                {note.Notes}
              </Text>
              {index < 19 && (
                <Divider borderColor="gray.300" mt="20px" mb="20px" />
              )}
            </Box>
          ))}
        <div ref={endOfNotesRef}></div>
      </Box>
      <HStack
        width="100%"
        height="30px"
        justifyContent="flex-start"
        spacing="50px"
      >
        <Checkbox
          size="lg"
          isChecked={recruiterChecked}
          onChange={(e) => setRecruiterChecked(e.target.checked)}
        >
          Recruiter
        </Checkbox>
        <Checkbox
          size="lg"
          isChecked={processorChecked}
          onChange={(e) => setProcessorChecked(e.target.checked)}
        >
          Processor
        </Checkbox>
      </HStack>
      <HStack
        width="100%"
        height="100px"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add note..."
          resize="none"
          width="100%"
          borderColor="blackAlpha.300"
          maxLength={999}
        />
        <IconButton
          icon={<IoIosSend />}
          colorScheme="facebook"
          aria-label="Send"
          size="sm"
          borderRadius="full"
          isLoading={noteLoading}
          isDisabled={!notes.trim()}
          onClick={handleSendNote}
        />
      </HStack>
    </VStack>
  );
};

export default DriverBoardApprovalNotes;
